import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import Webcam from "react-webcam";
import Layout from "./Layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RiseLoader } from "react-spinners";
import { apiManager } from "../api/API_Manager";
import { waitingTime } from "../util/constants";
import { Spinner } from "react-bootstrap";

export default function JoinMeeting() {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Helper function to exit and close window
  const exitAndClose = (message, isError = true) => {
    if (message) {
      if (isError) {
        toast.error(message);
      } else {
        toast.warn(message);
      }
    }
    
    // Try to close window after showing toast
    setTimeout(() => {
      window.close();
      
      // Fallback: navigate to home if window.close() doesn't work
      setTimeout(() => {
        if (!window.closed) {
          navigate('/');
        }
      }, 500);
    }, 2000); // 2 second delay to show toast
  };

  const askCameraPermission = () => {
    const permissions = navigator.mediaDevices.getUserMedia({ video: true });

    permissions
      .then(() => {
        setCamButton({ ...camButton, off: false });
      })
      .catch((err) => {
        setCamButton({ ...camButton, altText: "Permission denied" });
        console.error(err);
      });
  };

  const askMicPermission = () => {
    const permissions = navigator.mediaDevices.getUserMedia({ audio: true });

    permissions
      .then(() => {
        setMicButton({ ...micButton, off: false });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const [params] = useSearchParams();
  const organizer = params.get("organizer") === "true";
  const roomInURL = params.get("room_id") !== null;
  const isError = params.get("error") === "Forbidden";
  const joinPollRef = useRef(null);

  const [micButton, setMicButton] = useState({ color: "white", off: true });
  const [camButton, setCamButton] = useState({
    color: "white",
    off: true,
    altText: "Camera is off",
  });

  const joinSchema = yup.object().shape({
    name: yup.string().required().label("Name"),
    password: yup.string().required().label("Password"),
    room_id: yup.string().required().label("Room ID"),
  });

  const {
    control: joinControls,
    setValue: setJoinValues,
    handleSubmit: handleJoinMeeting,
    setError: setJoinError,
    formState: { errors: joinErrors },
  } = useForm({
    defaultValues: {
      password: organizer ? "" : "Abcd@123",
      name: "",
      room_id: roomInURL ? params.get("room_id") : "",
    },
    mode: "onBlur",
    resolver: yupResolver(joinSchema),
  });

  const clearForm = () => {
    setJoinValues("password", "");
    setJoinValues("name", "");
    setJoinValues("room_id", "");

    setJoinError("password", "");
    setJoinError("name", "");
    setJoinError("room_id", "");
  };

  const handleMicToggleClick = () => {
    if (micButton.off) {
      askMicPermission();
      setMicButton({ ...micButton, off: !micButton.off });
    } else {
      setMicButton({ ...micButton, off: !micButton.off });
    }
  };
  const handleCamToggleClick = () => {
    if (camButton.off) {
      askCameraPermission();
      setCamButton({ ...camButton, off: !camButton.off });
    } else {
      setCamButton({
        ...camButton,
        altText: "Camera is off",
        off: !camButton.off,
      });
    }
  };

  const generateCreatorToken = (data) => {
    apiManager("POST", "generateCreatorToken", data)
      .then((res) => {
        if (res.status) {
          localStorage.setItem("liveKitToken", res.data.liveKitToken);
          localStorage.setItem("accessToken", res.data.accessToken);
          localStorage.setItem("role", res.data.role);
          localStorage.setItem("room_id", res.data.room);
          localStorage.setItem("record_room_data", JSON.stringify(data));
          navigate("/meet", {
            state: { camToggle: !camButton.off, micToggle: !micButton.off },
          });
        } else {
          clearForm();
          setFullPageLoading(false);
          exitAndClose("Could not enter the room.");
        }
      })
      .catch((err) => {
        console.error(err);
        setFullPageLoading(false);
        exitAndClose("Something went wrong!");
      });
  };

  const letMeIn = async (data) => {
    const res = await apiManager("POST", "letMeIn", data, {
      accesstoken: localStorage.getItem("accessToken"),
    });

    if (res.status) {
      localStorage.setItem("liveKitToken", res.data.liveKitToken);
      localStorage.setItem("accessToken", res.data.accessToken);
      localStorage.setItem("requestId", res.data.requestId);
      localStorage.setItem("identity", res.data.identity);
      localStorage.setItem("role", res.data.role);

      navigate("/meet", {
        state: { camToggle: !camButton.off, micToggle: !micButton.off },
      });
      return "approved";
    }

    return res.message === "Pending" ? "pending" : "rejected";
  };

  // Request Loading
  const [btnClickedToRequest, setBtnClickedToRequest] = useState(false);
  const handleJoin = (data) => {
    setBtnClickedToRequest(true);
    if (organizer) {
      const apiData = {
        password: data.password,
        room_id: data.room_id,
        fullName: data.name,
      };
      apiManager("POST", "generateOrganizerToken", apiData)
        .then((res) => {
          if (res.status) {
            localStorage.setItem("liveKitToken", res.data.liveKitToken);
            localStorage.setItem("accessToken", res.data.accessToken);
            localStorage.setItem("room_id", res.data.room);
            navigate("/meet", {
              state: { camToggle: !camButton.off, micToggle: !micButton.off },
            });
          } else {
            toast.error("Could not get in.");
          }
          setBtnClickedToRequest(false);
        })
        .catch((err) => {
          console.error(err);
          setBtnClickedToRequest(false);
          toast.error("Something went wrong!");
        });
    } else {
      const apiData = {
        room_id: data.room_id,
        fullName: data.name,
      };
      apiManager("POST", "joinRequest", apiData)
        .then((res) => {
          if (res.status) {
            setLoading(true);
            toast.success(res.message);
            localStorage.setItem("accessToken", res.data.accessToken);
            localStorage.setItem("room_id", res.data.room);
            requestJoin({ requestId: res.data.requestId });
            setBtnClickedToRequest(false);
          } else {
            toast.error(res.message);
            setBtnClickedToRequest(false);
          }
        })
        .catch((err) => {
          console.error(err);
          setBtnClickedToRequest(false);
          toast.error("Something went wrong!");
        });
    }
  };

  const [loading, setLoading] = useState(false);
  const clearJoinPoll = () => {
    if (joinPollRef.current) {
      clearInterval(joinPollRef.current);
      joinPollRef.current = null;
    }
  };

  const requestJoin = (data) => {
    setLoading(true);
    let attempts = 1;

    clearJoinPoll();

    const poll = async () => {
      try {
        const status = await letMeIn(data);

        if (status === "approved") {
          clearJoinPoll();
          setLoading(false);
          return;
        }

        if (status === "rejected") {
          clearJoinPoll();
          setLoading(false);
          exitAndClose("Your request was rejected by the meeting organizer.", false);
          return;
        }

        if (attempts >= waitingTime * 12) {
          clearJoinPoll();
          clearForm();
          setLoading(false);
          exitAndClose("Your request to join has expired, try again.");
          return;
        }

        attempts++;
      } catch (err) {
        console.error(err);
        clearJoinPoll();
        setLoading(false);
        exitAndClose("Something went wrong!");
      }
    };

    poll();
    joinPollRef.current = setInterval(poll, 5000);
  };

  const [fullPageLoading, setFullPageLoading] = useState(false);

  useEffect(() => {
    const room_id = params.get("room_id");
    const actionToken = params.get("actionToken");

    if (isError) {
      exitAndClose(
        "Forbidden, Please contact support if you think this was a mistake."
      );
    } else {
      if (room_id && actionToken) {
        setFullPageLoading(true);
        generateCreatorToken({ room_id: room_id, actionToken: actionToken });
      }
    }
    return clearJoinPoll;
    // This effect handles the initial URL action token only once on page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (fullPageLoading) {
    return (
      <div className="container">
        <div className="row">
          <div
            className="col-lg-12 d-flex justify-content-around"
            style={{ marginTop: "150px" }}
          >
            <RiseLoader loading={fullPageLoading} size={30} color="blue" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="" style={{ minHeight: "calc(100vh - 200px)", paddingBottom: "20px" }}>
        <div className="container">
          <div
            className="row g-3"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "10px",
            }}
          >
            <div className="col-12 col-lg-6 d-flex justify-content-center">
              <div className="camPreview">
                {!camButton.off ? (
                  <>
                    <Webcam audio={!micButton.off} mirrored />
                  </>
                ) : (
                  <>
                    <div className="row text-center">
                      <span className="camOffText fw-bold fs-4">
                        {camButton.altText}
                      </span>
                    </div>
                  </>
                )}
                <div className="d-flex w-100 justify-content-evenly align-items-center toggleButtons">
                  <div>
                    <Icon
                      className="mx-2 toggleButton"
                      style={{
                        cursor: "pointer",
                        background: !micButton.off ? "transparent" : "#fa3343",
                        border: !micButton.off ? "1px solid white" : "none",
                      }}
                      onClick={handleMicToggleClick}
                      onMouseEnter={() =>
                        setMicButton({ ...micButton, color: "white" })
                      }
                      onMouseLeave={() =>
                        setMicButton({ ...micButton, color: "white" })
                      }
                      icon={
                        micButton.off
                          ? "material-symbols:mic-off"
                          : "material-symbols:mic"
                      }
                      color={micButton.color}
                      width="40"
                    />
                    <Icon
                      className="mx-2 toggleButton"
                      style={{
                        cursor: "pointer",
                        background: !camButton.off ? "transparent" : "#fa3343",
                        border: !camButton.off ? "1px solid white" : "none",
                      }}
                      onClick={handleCamToggleClick}
                      onMouseEnter={() =>
                        setCamButton({ ...camButton, color: "white" })
                      }
                      onMouseLeave={() =>
                        setCamButton({ ...camButton, color: "white" })
                      }
                      icon={camButton.off ? "mdi:camera-off" : "mdi:camera"}
                      color={camButton.color}
                      width="40"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-5 entries">
              {loading ? (
                <>
                  <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                      minHeight: "300px",
                      padding: "20px",
                    }}
                  >
                    <div
                      className="mb-4"
                    >
                      <RiseLoader loading={loading} size={15} color="blue" />
                    </div>
                    <div className="text-center px-3">
                      <h5 className="fs-5 fs-md-4">
                        Waiting for meeting organizer to let you in.
                      </h5>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h5
                    className="mb-3 text-center"
                    style={{ 
                      fontWeight: "400",
                      fontSize: "clamp(1.5rem, 5vw, 2.5rem)",
                      marginTop: "0.5rem"
                    }}
                  >
                    Ready to join..?
                  </h5>
                  <div className="mt-2">
                    <div className="mb-4">
                      <label className="form-label" style={{ fontWeight: "500", marginBottom: "8px" }}>Display name</label>
                      <Controller
                        name="name"
                        control={joinControls}
                        rules={{ required: true }}
                        render={({ field: { value, onChange, onBlur } }) => (
                          <input
                            placeholder="John Doe"
                            type="text"
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            className="form-control"
                            id="displayName"
                            autoComplete="name"
                            autoFocus
                          />
                        )}
                      />
                      {joinErrors.name && (
                        <span style={{ color: "red", fontSize: "12px" }}>
                          {joinErrors.name.message}
                        </span>
                      )}
                    </div>

                    {!roomInURL && (
                      <>
                        <div className="mb-4">
                          <label className="form-label" style={{ fontWeight: "500", marginBottom: "8px" }}>Room ID</label>
                          <Controller
                            name="room_id"
                            control={joinControls}
                            rules={{ required: true }}
                            render={({
                              field: { value, onChange, onBlur },
                            }) => (
                              <input
                                placeholder="Enter room ID"
                                type="text"
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                className="form-control"
                                id="roomId"
                                autoComplete="off"
                              />
                            )}
                          />
                          {joinErrors.room_id && (
                            <span style={{ color: "red", fontSize: "12px" }}>
                              {joinErrors.room_id.message}
                            </span>
                          )}
                        </div>
                      </>
                    )}

                    {organizer ? (
                      <>
                        <div className="mb-4">
                          <label className="form-label" style={{ fontWeight: "500", marginBottom: "8px" }}>
                            Organizer Password
                          </label>
                          <Controller
                            name="password"
                            control={joinControls}
                            rules={{ required: true }}
                            render={({
                              field: { value, onChange, onBlur },
                            }) => (
                              <div style={{ position: "relative" }}>
                                <input
                                  placeholder="Enter password"
                                  type={showPassword ? "text" : "password"}
                                  value={value}
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  className="form-control"
                                  id="organizerPassword"
                                  autoComplete="current-password"
                                  style={{ paddingRight: "48px" }}
                                />
                                <span
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    right: "12px",
                                    cursor: "pointer",
                                    padding: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? (
                                    <Icon icon={"mdi:eye"} fontSize={22} />
                                  ) : (
                                    <Icon
                                      icon={"basil:eye-closed-solid"}
                                      fontSize={22}
                                    />
                                  )}
                                </span>
                              </div>
                            )}
                          />
                          {joinErrors.password && (
                            <span style={{ color: "red", fontSize: "12px" }}>
                              {joinErrors.password.message}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* <div className="mb-3">
                                            <label className="form-label">Email address (optional)</label>
                                            <Controller
                                                name='email'
                                                control={joinControls}
                                                rules={{ required: true }}
                                                render={({ field: { value, onChange, onBlur } }) => (
                                                    <input placeholder='abc@gmail.com' type="email" value={value} onChange={onChange} onBlur={onBlur} className="form-control" id="exampleInputEmail1" aria-describedby="emailHelp" />
                                                )}
                                            />
                                            {joinErrors.email && <span style={{ color: 'red', fontSize: '12px' }}>{joinErrors.email.message}</span>}
                                        </div> */}
                      </>
                    )}
                    <div className="d-flex justify-content-center mt-3 mt-md-5">
                      <button
                        className="btn btn-primary rounded-5 mobile-full-width-btn"
                        style={{
                          backgroundColor: "#235aff",
                          paddingTop: btnClickedToRequest ? "8px" : undefined,
                          paddingBottom: btnClickedToRequest ? "8px" : "12px",
                          minWidth: "200px",
                          fontSize: 20,
                          fontWeight: 500,
                          height: "50px",
                        }}
                        disabled={btnClickedToRequest}
                        onClick={handleJoinMeeting(handleJoin)}
                      >
                        {btnClickedToRequest ? <Spinner /> : "Request"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
